import { compileFormula, evaluateFormula, isTruthy, type FormulaContext, type FormulaValue } from './formula';
import { emptyCell, type CellBorders, type CellIcon, type FontStyle, type SheetCell, type SheetMerge } from './types';

export interface DxfStyle {
  font?: FontStyle;
  background?: string;
  borders?: CellBorders;
}

export interface CfValue {
  type: string;
  value?: string;
  gte: boolean;
}

export interface CfRule {
  type: string;
  priority: number;
  stopIfTrue: boolean;
  dxf?: DxfStyle;
  operator?: string;
  formulas: string[];
  text?: string;
  rank?: number;
  percent?: boolean;
  bottom?: boolean;
  aboveAverage?: boolean;
  equalAverage?: boolean;
  stdDev?: number;
  timePeriod?: string;
  colorScale?: { values: CfValue[]; colors: string[] };
  dataBar?: { values: CfValue[]; color: string; showValue: boolean; minLength: number; maxLength: number };
  iconSet?: { name: string; values: CfValue[]; reverse: boolean; showValue: boolean };
}

export interface CfBlock {
  ranges: SheetMerge[];
  rules: CfRule[];
}

const EXPLICIT_LIMIT = 1000;
const CELL_BUDGET = 1_000_000;
const RANGE_READ_BUDGET = 5_000_000;

const RED = '#D9302C';
const YELLOW = '#F2B800';
const GREEN = '#3E9A3E';
const GRAY = '#7F7F7F';
const BLACK = '#404040';
const PINK = '#F08080';

const ICON_SETS: Record<string, [string, string][]> = {
  '3Arrows': [
    ['↓', RED],
    ['→', YELLOW],
    ['↑', GREEN],
  ],
  '3ArrowsGray': [
    ['↓', GRAY],
    ['→', GRAY],
    ['↑', GRAY],
  ],
  '3Flags': [
    ['⚑', RED],
    ['⚑', YELLOW],
    ['⚑', GREEN],
  ],
  '3TrafficLights1': [
    ['●', RED],
    ['●', YELLOW],
    ['●', GREEN],
  ],
  '3TrafficLights2': [
    ['●', RED],
    ['●', YELLOW],
    ['●', GREEN],
  ],
  '3Signs': [
    ['◆', RED],
    ['▲', YELLOW],
    ['●', GREEN],
  ],
  '3Symbols': [
    ['✖', RED],
    ['!', YELLOW],
    ['✔', GREEN],
  ],
  '3Symbols2': [
    ['✖', RED],
    ['!', YELLOW],
    ['✔', GREEN],
  ],
  '3Stars': [
    ['☆', YELLOW],
    ['✮', YELLOW],
    ['★', YELLOW],
  ],
  '3Triangles': [
    ['▼', RED],
    ['▬', YELLOW],
    ['▲', GREEN],
  ],
  '4Arrows': [
    ['↓', RED],
    ['↘', YELLOW],
    ['↗', YELLOW],
    ['↑', GREEN],
  ],
  '4ArrowsGray': [
    ['↓', GRAY],
    ['↘', GRAY],
    ['↗', GRAY],
    ['↑', GRAY],
  ],
  '4RedToBlack': [
    ['●', BLACK],
    ['●', GRAY],
    ['●', PINK],
    ['●', RED],
  ],
  '4TrafficLights': [
    ['●', BLACK],
    ['●', RED],
    ['●', YELLOW],
    ['●', GREEN],
  ],
  '4Rating': [
    ['◔', GRAY],
    ['◑', GRAY],
    ['◕', GRAY],
    ['●', GRAY],
  ],
  '5Arrows': [
    ['↓', RED],
    ['↘', YELLOW],
    ['→', YELLOW],
    ['↗', YELLOW],
    ['↑', GREEN],
  ],
  '5ArrowsGray': [
    ['↓', GRAY],
    ['↘', GRAY],
    ['→', GRAY],
    ['↗', GRAY],
    ['↑', GRAY],
  ],
  '5Quarters': [
    ['○', BLACK],
    ['◔', BLACK],
    ['◑', BLACK],
    ['◕', BLACK],
    ['●', BLACK],
  ],
  '5Rating': [
    ['○', GRAY],
    ['◔', GRAY],
    ['◑', GRAY],
    ['◕', GRAY],
    ['●', GRAY],
  ],
};

function cellValue(cell?: SheetCell): FormulaValue {
  if (!cell || cell.kind === 'empty') {
    return null;
  }
  return cell.value ?? cell.text;
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) {
    return 0;
  }
  const position = (sorted.length - 1) * Math.min(Math.max(p, 0), 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function hexToRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function mix(from: string, to: string, ratio: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `#${a
    .map((channel, index) =>
      Math.round(channel + (b[index] - channel) * ratio)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

function compareValues(a: FormulaValue, b: FormulaValue): number {
  const left = a ?? (typeof b === 'string' ? '' : 0);
  const right = b ?? (typeof a === 'string' ? '' : 0);
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return left.toLowerCase().localeCompare(right.toLowerCase());
  }
  return typeof left === 'number' ? -1 : 1;
}

function serialOf(date: Date): number {
  return (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(1899, 11, 30)) / 86400000;
}

function inTimePeriod(serial: number, period: string, date1904: boolean): boolean {
  const day = Math.floor(date1904 ? serial + 1462 : serial);
  const now = new Date();
  const today = serialOf(now);
  const weekStart = today - now.getDay();
  const monthStart = serialOf(new Date(now.getFullYear(), now.getMonth(), 1));
  const nextMonthStart = serialOf(new Date(now.getFullYear(), now.getMonth() + 1, 1));
  const lastMonthStart = serialOf(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  const afterNextMonth = serialOf(new Date(now.getFullYear(), now.getMonth() + 2, 1));
  switch (period) {
    case 'today':
      return day === today;
    case 'yesterday':
      return day === today - 1;
    case 'tomorrow':
      return day === today + 1;
    case 'last7Days':
      return day > today - 7 && day <= today;
    case 'thisWeek':
      return day >= weekStart && day < weekStart + 7;
    case 'lastWeek':
      return day >= weekStart - 7 && day < weekStart;
    case 'nextWeek':
      return day >= weekStart + 7 && day < weekStart + 14;
    case 'thisMonth':
      return day >= monthStart && day < nextMonthStart;
    case 'lastMonth':
      return day >= lastMonthStart && day < monthStart;
    case 'nextMonth':
      return day >= nextMonthStart && day < afterNextMonth;
  }
  return false;
}

interface CellState {
  stopped: boolean;
  locked: Set<string>;
}

export function applyConditionalFormats(rows: SheetCell[][], blocks: CfBlock[], date1904: boolean): void {
  if (!blocks.length) {
    return;
  }
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const states = new Map<SheetCell, CellState>();
  const getValue = (row: number, col: number) => cellValue(rows[row]?.[col]);
  const budget = { cells: CELL_BUDGET, reads: RANGE_READ_BUDGET };

  const entries = blocks
    .flatMap((block) => block.rules.map((rule) => ({ rule, ranges: block.ranges })))
    .sort((a, b) => a.rule.priority - b.rule.priority);

  entries.forEach(({ rule, ranges }) => {
    const origin = ranges[0];
    if (!origin) {
      return;
    }
    const cells: { row: number; col: number; cell: SheetCell }[] = [];
    ranges.forEach((range) => {
      const rowTo = range.rowTo - range.rowFrom < EXPLICIT_LIMIT ? range.rowTo : rows.length - 1;
      const colTo = range.colTo - range.colFrom < EXPLICIT_LIMIT ? range.colTo : columnCount - 1;
      for (let row = range.rowFrom; row <= rowTo && budget.cells > 0; row++) {
        while (rows.length <= row) {
          rows.push([]);
        }
        const rowCells = rows[row];
        for (let col = range.colFrom; col <= colTo && budget.cells > 0; col++) {
          budget.cells -= Math.max(col - rowCells.length, 0) + 1;
          while (rowCells.length <= col) {
            rowCells.push(emptyCell());
          }
          cells.push({ row, col, cell: rowCells[col] });
        }
      }
    });
    if (!cells.length) {
      return;
    }

    const context = (row: number, col: number): FormulaContext => ({
      row,
      col,
      originRow: origin.rowFrom,
      originCol: origin.colFrom,
      getValue,
      date1904,
      rowCount: rows.length,
      columnCount,
      budget,
    });
    const evaluate = (source: string, row: number, col: number): FormulaValue | undefined => {
      const node = compileFormula(source);
      return node ? evaluateFormula(node, context(row, col)) : undefined;
    };

    const numbers = cells
      .map(({ cell }) => cell.value)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .sort((a, b) => a - b);

    const threshold = (value: CfValue, fallback: 'min' | 'max'): number => {
      const min = numbers[0] ?? 0;
      const max = numbers[numbers.length - 1] ?? 0;
      switch (value.type) {
        case 'min':
        case 'autoMin':
          return min;
        case 'max':
        case 'autoMax':
          return max;
        case 'percent':
          return min + ((max - min) * Number(value.value ?? 0)) / 100;
        case 'percentile':
          return percentile(numbers, Number(value.value ?? 0) / 100);
        case 'num':
        case 'formula': {
          const direct = Number(value.value);
          if (!Number.isNaN(direct)) {
            return direct;
          }
          const result = evaluate(value.value ?? '', origin.rowFrom, origin.colFrom);
          return typeof result === 'number' ? result : fallback === 'min' ? min : max;
        }
      }
      return fallback === 'min' ? min : max;
    };

    const stateOf = (cell: SheetCell): CellState => {
      let state = states.get(cell);
      if (!state) {
        state = { stopped: false, locked: new Set() };
        states.set(cell, state);
      }
      return state;
    };

    const average = numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : 0;
    const deviation = numbers.length
      ? Math.sqrt(numbers.reduce((sum, value) => sum + (value - average) ** 2, 0) / Math.max(numbers.length - 1, 1))
      : 0;

    let topLimit: number | undefined;
    if (rule.type === 'top10' && numbers.length) {
      const count = rule.percent
        ? Math.max(1, Math.floor((numbers.length * (rule.rank ?? 10)) / 100))
        : Math.min(rule.rank ?? 10, numbers.length);
      topLimit = rule.bottom ? numbers[count - 1] : numbers[numbers.length - count];
    }

    let counts: Map<string, number> | undefined;
    if (rule.type === 'duplicateValues' || rule.type === 'uniqueValues') {
      counts = new Map();
      cells.forEach(({ cell }) => {
        if (cell.kind === 'empty') {
          return;
        }
        const key = String(cellValue(cell)).toLowerCase();
        counts.set(key, (counts.get(key) ?? 0) + 1);
      });
    }

    const scaleBounds = rule.colorScale?.values.map((value, index, all) =>
      threshold(value, index === all.length - 1 ? 'max' : 'min'),
    );
    const barBounds = rule.dataBar
      ? [threshold(rule.dataBar.values[0], 'min'), threshold(rule.dataBar.values[1], 'max')]
      : null;
    const iconBounds = rule.iconSet?.values.map((value) => threshold(value, 'min'));

    cells.forEach(({ row, col, cell }) => {
      const state = stateOf(cell);
      if (state.stopped) {
        return;
      }
      const value = cellValue(cell);
      const text = value === null ? '' : String(value);
      let matched = false;

      switch (rule.type) {
        case 'cellIs': {
          const first = evaluate(rule.formulas[0] ?? '', row, col) ?? null;
          const second = evaluate(rule.formulas[1] ?? '', row, col) ?? null;
          const result = compareValues(value, first);
          switch (rule.operator) {
            case 'lessThan':
              matched = result < 0;
              break;
            case 'lessThanOrEqual':
              matched = result <= 0;
              break;
            case 'greaterThan':
              matched = result > 0;
              break;
            case 'greaterThanOrEqual':
              matched = result >= 0;
              break;
            case 'notEqual':
              matched = result !== 0;
              break;
            case 'between':
            case 'notBetween': {
              const low = compareValues(first, second) <= 0 ? first : second;
              const high = low === first ? second : first;
              const inside = compareValues(value, low) >= 0 && compareValues(value, high) <= 0;
              matched = rule.operator === 'between' ? inside : !inside;
              break;
            }
            default:
              matched = result === 0;
          }
          break;
        }
        case 'expression':
          matched = isTruthy(evaluate(rule.formulas[0] ?? '', row, col));
          break;
        case 'containsText':
          matched = text.toLowerCase().includes((rule.text ?? '').toLowerCase());
          break;
        case 'notContainsText':
          matched = !text.toLowerCase().includes((rule.text ?? '').toLowerCase());
          break;
        case 'beginsWith':
          matched = text.toLowerCase().startsWith((rule.text ?? '').toLowerCase());
          break;
        case 'endsWith':
          matched = text.toLowerCase().endsWith((rule.text ?? '').toLowerCase());
          break;
        case 'containsBlanks':
          matched = text.trim() === '';
          break;
        case 'notContainsBlanks':
          matched = text.trim() !== '';
          break;
        case 'containsErrors':
          matched = cell.kind === 'error';
          break;
        case 'notContainsErrors':
          matched = cell.kind !== 'error';
          break;
        case 'timePeriod':
          matched = typeof value === 'number' && inTimePeriod(value, rule.timePeriod ?? '', date1904);
          break;
        case 'top10':
          matched =
            typeof value === 'number' &&
            topLimit !== undefined &&
            (rule.bottom ? value <= topLimit : value >= topLimit);
          break;
        case 'aboveAverage': {
          if (typeof value !== 'number') {
            break;
          }
          const shift = (rule.stdDev ?? 0) * deviation;
          const above = rule.aboveAverage !== false;
          if (above) {
            matched = rule.equalAverage ? value >= average + shift : value > average + shift;
          } else {
            matched = rule.equalAverage ? value <= average - shift : value < average - shift;
          }
          break;
        }
        case 'duplicateValues':
        case 'uniqueValues': {
          if (cell.kind === 'empty') {
            break;
          }
          const count = counts.get(text.toLowerCase()) ?? 0;
          matched = rule.type === 'duplicateValues' ? count > 1 : count === 1;
          break;
        }
        case 'colorScale': {
          if (typeof value !== 'number' || !scaleBounds || state.locked.has('background')) {
            break;
          }
          const colors = rule.colorScale.colors;
          let color = colors[0];
          if (value >= scaleBounds[scaleBounds.length - 1]) {
            color = colors[colors.length - 1];
          } else if (value > scaleBounds[0]) {
            for (let i = 0; i < scaleBounds.length - 1; i++) {
              if (value <= scaleBounds[i + 1]) {
                const span = scaleBounds[i + 1] - scaleBounds[i];
                color = mix(colors[i], colors[i + 1], span ? (value - scaleBounds[i]) / span : 0);
                break;
              }
            }
          }
          cell.style = { ...cell.style, background: color };
          state.locked.add('background');
          break;
        }
        case 'dataBar': {
          if (typeof value !== 'number' || !barBounds || cell.dataBar) {
            break;
          }
          const [min, max] = barBounds;
          const position = max === min ? 0.5 : Math.min(Math.max((value - min) / (max - min), 0), 1);
          const { minLength, maxLength, color, showValue } = rule.dataBar;
          cell.dataBar = { color, ratio: (minLength + (maxLength - minLength) * position) / 100, gradient: true };
          if (!showValue) {
            cell.text = '';
          }
          break;
        }
        case 'iconSet': {
          if (typeof value !== 'number' || !iconBounds || cell.icon) {
            break;
          }
          const icons = ICON_SETS[rule.iconSet.name] ?? ICON_SETS['3TrafficLights1'];
          let bucket = 0;
          iconBounds.forEach((bound, index) => {
            const inclusive = rule.iconSet.values[index]?.gte !== false;
            if (index > 0 && (inclusive ? value >= bound : value > bound)) {
              bucket = index;
            }
          });
          const ordered = rule.iconSet.reverse ? [...icons].reverse() : icons;
          const [symbol, color] = ordered[Math.min(bucket, ordered.length - 1)];
          const icon: CellIcon = { symbol, color, hideValue: !rule.iconSet.showValue };
          cell.icon = icon;
          break;
        }
      }

      if (!matched || !rule.dxf) {
        return;
      }
      const style = { ...cell.style };
      const font = rule.dxf.font ?? {};
      (Object.keys(font) as (keyof FontStyle)[]).forEach((key) => {
        if (font[key] === undefined || state.locked.has(key)) {
          return;
        }
        (style as Record<string, unknown>)[key] = font[key];
        state.locked.add(key);
        if (key === 'color') {
          cell.formatColor = undefined;
        }
      });
      if (rule.dxf.background && !state.locked.has('background')) {
        style.background = rule.dxf.background;
        state.locked.add('background');
      }
      if (rule.dxf.borders && !state.locked.has('borders')) {
        style.borders = { ...style.borders, ...rule.dxf.borders };
        state.locked.add('borders');
      }
      cell.style = style;
      if (rule.stopIfTrue) {
        state.stopped = true;
      }
    });
  });
}
